import { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import Button from "@shared/v2/Button";
import Dropdown from "@shared/v2/Dropdown";
import Modal from "@shared/v2/Modal";
import TextLink from "@shared/v2/TextLink";
import {
  ADMIN_INBOUND_RECORDING_OPTIONS,
  ADMIN_INBOUND_RECORDING_OPTIONS_ADJUSTED,
  ADMIN_OUTBOUND_RECORDING_OPTIONS,
  ADMIN_OUTBOUND_RECORDING_OPTIONS_ADJUSTED,
} from "./helpers/recordingOptions";
import {
  inboundRecordingNotificationOptions,
  OUTBOUND_INBOUND_RECORDING_NOTIFICATION_OPTIONS,
  outboundRecordingNotificationOptions,
} from "./helpers/recordingNotificationOptions";
import { CustomOption } from "./CallRecordingDropdown";
import {
  useAccountCallRecordingLoading,
  useAccountCallRecordingSettings,
  useFeatureFlags,
  useIsPlace,
  useIsTwoPartyState,
} from "../../reducers/layoutReducer/selectors";
import { updateAccountRecordingSettings } from "../../reducers/layoutReducer";
import PlayMessage from "./PlayMessage";
import NoticeTwoPartyConsent from "./NoticeTwoPartyConsent";
import AiSparkleBanner from "./AiSparkleBanner";

const TeamCallRecordingSettingsModal = ({ onClose, open }) => {
  const dispatch = useDispatch();
  const loading = useAccountCallRecordingLoading();
  const accountCallRecordingSettings = useAccountCallRecordingSettings();
  const featureFlags = useFeatureFlags();
  const isPlace = useIsPlace();
  const isTwoPartyState = useIsTwoPartyState();
  const callRecordingAdjustmentsEnabled = featureFlags?.callRecordingAdjustments;
  const [formData, setFormData] = useState({});

  const showTwoPartyNotice =
    formData.inboundNotificationMessageCondition !== "always" ||
    formData.outboundNotificationMessageCondition !== "always";

  const initialAgreeTwoParty =
    accountCallRecordingSettings.inboundNotificationMessageCondition !== "always" ||
    accountCallRecordingSettings.outboundNotificationMessageCondition !== "always";
  const [agreeTwoParty, setAgreeTwoParty] = useState(initialAgreeTwoParty);

  const setFormValue = (optOrEvent, action) => {
    const target = optOrEvent?.target;
    setFormData((prev) => ({
      ...prev,
      [action?.name || target.name]: optOrEvent.value || target.checked,
    }));
  };

  useEffect(() => {
    if (showTwoPartyNotice) setAgreeTwoParty(initialAgreeTwoParty);
  }, [showTwoPartyNotice]);

  useEffect(() => {
    if (accountCallRecordingSettings && open) {
      setFormData(accountCallRecordingSettings);
      setAgreeTwoParty(initialAgreeTwoParty);
    }
  }, [accountCallRecordingSettings, open]);

  const inboundRecordingOptions = callRecordingAdjustmentsEnabled
    ? ADMIN_INBOUND_RECORDING_OPTIONS_ADJUSTED
    : ADMIN_INBOUND_RECORDING_OPTIONS;
  const outboundRecordingOptions = callRecordingAdjustmentsEnabled
    ? ADMIN_OUTBOUND_RECORDING_OPTIONS_ADJUSTED
    : ADMIN_OUTBOUND_RECORDING_OPTIONS;
  const outboundNotificationOptions = callRecordingAdjustmentsEnabled
    ? OUTBOUND_INBOUND_RECORDING_NOTIFICATION_OPTIONS
    : outboundRecordingNotificationOptions(isPlace, isTwoPartyState);
  const inboundNotificationOptions = callRecordingAdjustmentsEnabled
    ? OUTBOUND_INBOUND_RECORDING_NOTIFICATION_OPTIONS
    : inboundRecordingNotificationOptions(isPlace, isTwoPartyState);

  return (
    <Modal
      className="tw-flex tw-justify-center tw-items-center"
      dialogClassName="tw-max-w-[762px] tw-w-full"
      contentClassName="tw-w-[762px] tw-max-h-[90vh] tw-flex"
      show={open}
      onHide={onClose}
    >
      <form
        className="tw-flex tw-flex-col tw-gap-[32px]"
        onSubmit={(e) => {
          e.preventDefault();
          dispatch(updateAccountRecordingSettings(formData)).then(onClose);
        }}
      >
        <Modal.Header
          closeButton
          title="Team AI Call Recording Settings"
          description={
            <div>
              <span>
                These call recording settings will apply to outgoing calls made by all team members from the
                Brivity CRM, Brivity GO, and incoming calls to any Brivity Number. Learn more about{" "}
                <TextLink
                  href="https://www.brivityknowledge.com/docs/call-recording-ai-transcription-1"
                  target="_blank"
                >
                  recommended settings
                </TextLink>
                .
              </span>
              {!callRecordingAdjustmentsEnabled && isTwoPartyState && (
                <div className="tw-pt-[26px]">
                  <div className="tw-bg-gray-5 tw-border-[2px] tw-border-neutral-gray-10 tw-border-solid tw-rounded-[2px]">
                    <div className="tw-px-[16px] tw-py-[12px]">
                      <div className="tw-font-semibold tw-text-14d tw-text-gray-75">
                        Your business is located in a state that requires 2-party consent to record
                      </div>
                      <div className="tw-text-12d tw-text-gray-75">
                        Your settings are restricted to protect you from legal risk when recording phone
                        calls.
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          }
        />
        <Modal.Body className="tw-flex-1 tw-overflow-auto">
          <div className="!tw-grid tw-grid-cols-2 tw-gap-[40px] tw-justify-between tw-pb-[16px]">
            <div>
              <h3 className="tw-text-18d tw-font-bold tw-m-0 tw-pb-[16px]">Inbound Calls</h3>
              <Dropdown
                label="Inbound Recording"
                components={{ Option: CustomOption }}
                options={inboundRecordingOptions}
                value={inboundRecordingOptions.find((o) => o.value === formData.inboundRecording)}
                name="inboundRecording"
                menuShouldComeToFront
                onChange={setFormValue}
              />
              {callRecordingAdjustmentsEnabled && formData.inboundRecording !== "disabled" && (
                <div className="tw-mt-[12px]">
                  <AiSparkleBanner />
                </div>
              )}
            </div>
            <div>
              <h3 className="tw-text-18d tw-font-bold tw-m-0 tw-pb-[16px]">Outbound Calls</h3>
              <Dropdown
                label="Outbound Recording"
                components={{ Option: CustomOption }}
                options={outboundRecordingOptions}
                value={outboundRecordingOptions.find((o) => o.value === formData.outboundRecording)}
                name="outboundRecording"
                menuShouldComeToFront
                onChange={setFormValue}
                maxVisibleOptions={callRecordingAdjustmentsEnabled ? 10 : undefined}
              />
              {callRecordingAdjustmentsEnabled && formData.outboundRecording !== "disabled" && (
                <div className="tw-mt-[12px]">
                  <AiSparkleBanner />
                </div>
              )}
            </div>
          </div>

          <div className="tw-grid tw-grid-cols-2 tw-gap-[40px] tw-justify-between tw-pb-[16px]">
            {formData.inboundRecording !== "disabled" ? (
              <Dropdown
                label="Notification Message"
                components={{ Option: CustomOption }}
                options={inboundNotificationOptions}
                value={inboundNotificationOptions.find(
                  (o) => o.value === formData.inboundNotificationMessageCondition,
                )}
                name="inboundNotificationMessageCondition"
                menuShouldComeToFront
                onChange={setFormValue}
                toolTipPlacement="top"
                toolTipInfoContent={
                  <div className="tw-text-left tw-text-14d tw-w-[217px]">
                    Audio notification message must always play for businesses operating in states that
                    require 2-party consent to record phone calls.
                  </div>
                }
              />
            ) : (
              <div />
            )}
            {formData.outboundRecording !== "disabled" ? (
              <Dropdown
                label="Notification Message"
                components={{ Option: CustomOption }}
                options={outboundNotificationOptions}
                value={outboundNotificationOptions.find(
                  (o) => o.value === formData.outboundNotificationMessageCondition,
                )}
                name="outboundNotificationMessageCondition"
                menuShouldComeToFront
                onChange={setFormValue}
                toolTipPlacement="top"
                toolTipInfoContent={
                  <div className="tw-text-left tw-text-14d tw-w-[217px]">
                    Audio notification message must always play for businesses operating in states that
                    require 2-party consent to record phone calls.
                  </div>
                }
              />
            ) : (
              <div />
            )}
          </div>
          <div className="tw-grid tw-grid-cols-2 tw-gap-[40px] tw-justify-between tw-pb-[16px]">
            {formData.inboundRecording !== "disabled" ? <PlayMessage /> : <div />}
            {formData.outboundRecording !== "disabled" ? <PlayMessage /> : <div />}
          </div>

          <NoticeTwoPartyConsent
            showTwoPartyNotice={showTwoPartyNotice}
            agreeTwoParty={agreeTwoParty}
            setAgreeTwoParty={setAgreeTwoParty}
          />
        </Modal.Body>
        <Modal.Footer className="tw-flex tw-gap-[12px] tw-justify-between">
          <Button size="medium" schema="tertiary" disabled={loading} onClick={onClose}>
            Cancel
          </Button>
          <Button
            size="medium"
            type="submit"
            isLoading={loading}
            disabled={showTwoPartyNotice && !agreeTwoParty}
          >
            Save
          </Button>
        </Modal.Footer>
      </form>
    </Modal>
  );
};

TeamCallRecordingSettingsModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
};

export default TeamCallRecordingSettingsModal;
